import React, { useState } from 'react';
import ReactPlayer from 'react-player';
import { X } from 'lucide-react';
// Define types for our gallery items
type MediaType = 'image' | 'video';
interface GalleryItem {
  id: number;
  type: MediaType;
  thumbnail: string;
  source: string;
  title: string;
  category: string;
}
export function ProjectGallery() {
  const [selectedItem, setSelectedItem] = useState<GalleryItem | null>(null);
  // Sample gallery data with building maintenance related work
  const galleryItems: GalleryItem[] = [{
    id: 1,
    type: 'image',
    thumbnail: 'https://images.unsplash.com/photo-1581578731548-c64695cc6952?q=80&w=600&auto=format&fit=crop',
    source: 'https://images.unsplash.com/photo-1581578731548-c64695cc6952?q=80&w=1200&auto=format&fit=crop',
    title: 'Kitchen Renovation',
    category: 'Interior Decoration'
  }, {
    id: 2,
    type: 'image',
    thumbnail: 'https://images.unsplash.com/photo-1621886292650-520f76c747d6?q=80&w=600&auto=format&fit=crop',
    source: 'https://images.unsplash.com/photo-1621886292650-520f76c747d6?q=80&w=1200&auto=format&fit=crop',
    title: 'Electrical System Upgrade',
    category: 'Electrical Services'
  }, {
    id: 3,
    type: 'video',
    thumbnail: 'https://images.unsplash.com/photo-1584622650111-993a426fbf0a?q=80&w=600&auto=format&fit=crop',
    source: 'https://www.youtube.com/watch?v=Cz_n9dFj3js',
    title: 'Plumbing Repair Process',
    category: 'Plumbing Services'
  }, {
    id: 4,
    type: 'image',
    thumbnail: 'https://images.unsplash.com/photo-1565538810643-b5bdb714032a?q=80&w=600&auto=format&fit=crop',
    source: 'https://images.unsplash.com/photo-1565538810643-b5bdb714032a?q=80&w=1200&auto=format&fit=crop',
    title: 'Bathroom Remodeling',
    category: 'Flooring & Tiling'
  }, {
    id: 5,
    type: 'image',
    thumbnail: 'https://images.unsplash.com/photo-1604014238170-4def1e4e6fcf?q=80&w=600&auto=format&fit=crop',
    source: 'https://images.unsplash.com/photo-1604014238170-4def1e4e6fcf?q=80&w=1200&auto=format&fit=crop',
    title: 'Ceiling Repair',
    category: 'Ceiling & Walls'
  }, {
    id: 6,
    type: 'video',
    thumbnail: 'https://images.unsplash.com/photo-1581578731548-c64695cc6952?q=80&w=600&auto=format&fit=crop',
    source: 'https://www.youtube.com/watch?v=mLpfg-J-Hnc',
    title: 'AC Installation Guide',
    category: 'HVAC Services'
  }, {
    id: 7,
    type: 'image',
    thumbnail: 'https://images.unsplash.com/photo-1513694203232-719a280e022f?q=80&w=600&auto=format&fit=crop',
    source: 'https://images.unsplash.com/photo-1513694203232-719a280e022f?q=80&w=1200&auto=format&fit=crop',
    title: 'Custom Cabinetry',
    category: 'Carpentry & Woodwork'
  }, {
    id: 8,
    type: 'image',
    thumbnail: 'https://images.unsplash.com/photo-1542013936693-884638332954?q=80&w=600&auto=format&fit=crop',
    source: 'https://images.unsplash.com/photo-1542013936693-884638332954?q=80&w=1200&auto=format&fit=crop',
    title: 'Office Renovation',
    category: 'General Building Repairs'
  }];
  // Filter categories for the filter buttons
  const categories = Array.from(new Set(galleryItems.map(item => item.category)));
  // State for category filtering
  const [activeCategory, setActiveCategory] = useState<string>('All');
  const filteredItems = activeCategory === 'All' ? galleryItems : galleryItems.filter(item => item.category === activeCategory);
  return <section id="projects" className="py-16 md:py-24 bg-gray-50" data-id="element-176">
      <div className="container mx-auto px-4" data-id="element-177">
        <div className="text-center mb-12" data-id="element-178">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" data-id="element-179">
            Our Recent Projects
          </h2>
          <div className="w-24 h-1 bg-amber-500 mx-auto mb-6" data-id="element-180"></div>
          <p className="text-lg text-gray-700 max-w-3xl mx-auto" data-id="element-181">
            Browse through our portfolio of completed projects and see the
            quality of our work firsthand.
          </p>
        </div>
        {/* Category filter */}
        <div className="flex flex-wrap justify-center gap-3 mb-10" data-id="element-182">
          <button className={`px-4 py-2 rounded-md ${activeCategory === 'All' ? 'bg-amber-500 text-white' : 'bg-gray-200 text-gray-800 hover:bg-gray-300'} transition-colors`} onClick={() => setActiveCategory('All')} data-id="element-183">
            All Projects
          </button>
          {categories.map(category => <button key={category} className={`px-4 py-2 rounded-md ${activeCategory === category ? 'bg-amber-500 text-white' : 'bg-gray-200 text-gray-800 hover:bg-gray-300'} transition-colors`} onClick={() => setActiveCategory(category)} data-id="element-184">
              {category}
            </button>)}
        </div>
        {/* Gallery grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-185">
          {filteredItems.map(item => <div key={item.id} className="bg-white rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-shadow cursor-pointer group" onClick={() => setSelectedItem(item)} data-id="element-186">
              <div className="relative aspect-video overflow-hidden" data-id="element-187">
                <img src={item.thumbnail} alt={item.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" data-id="element-188" />
                {item.type === 'video' && <div className="absolute inset-0 flex items-center justify-center" data-id="element-189">
                    <div className="bg-amber-500 bg-opacity-80 rounded-full p-3" data-id="element-190">
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-white" data-id="element-191">
                        <polygon points="5 3 19 12 5 21 5 3" data-id="element-192" />
                      </svg>
                    </div>
                  </div>}
              </div>
              <div className="p-4" data-id="element-193">
                <h3 className="font-bold text-gray-900" data-id="element-194">{item.title}</h3>
                <p className="text-sm text-gray-500" data-id="element-195">{item.category}</p>
              </div>
            </div>)}
        </div>
      </div>
      {/* Modal for displaying selected item */}
      {selectedItem && <div className="fixed inset-0 bg-black bg-opacity-80 z-50 flex items-center justify-center p-4" data-id="element-196">
          <div className="relative max-w-6xl w-full max-h-[90vh] bg-white rounded-lg overflow-auto" data-id="element-197">
            <button onClick={() => setSelectedItem(null)} className="absolute right-4 top-4 bg-gray-800 text-white p-2 rounded-full z-10 hover:bg-gray-900 transition-colors" data-id="element-198">
              <X size={20} data-id="element-199" />
            </button>
            <div className="p-6" data-id="element-200">
              {selectedItem.type === 'image' ? <img src={selectedItem.source} alt={selectedItem.title} className="w-full h-auto max-h-[70vh] object-contain mx-auto" data-id="element-201" /> : <div className="aspect-video w-full" data-id="element-202">
                  <ReactPlayer url={selectedItem.source} width="100%" height="100%" controls={true} data-id="element-203" />
                </div>}
              <div className="mt-4" data-id="element-204">
                <h3 className="text-xl font-bold text-gray-900" data-id="element-205">
                  {selectedItem.title}
                </h3>
                <p className="text-gray-600" data-id="element-206">{selectedItem.category}</p>
              </div>
            </div>
          </div>
        </div>}
    </section>;
}